import { Shield, Lock, Eye, Database, Share2, Clock, UserCheck, Mail } from 'lucide-react';
import { LegalPageLayout, TocItem } from '@/components/legal/LegalPageLayout';

const TOC_ITEMS: TocItem[] = [
  { id: 'dados-coletados', title: '1. Dados Pessoais Coletados' },
  { id: 'bases-legais', title: '2. Bases Legais e Finalidades' },
  { id: 'seguranca', title: '3. Segurança e Teleconsultas' },
  { id: 'compartilhamento', title: '4. Compartilhamento de Dados' },
  { id: 'retencao', title: '5. Retenção e Guarda dos Dados' },
  { id: 'direitos', title: '6. Direitos do Titular (LGPD)' },
  { id: 'cookies', title: '7. Política de Cookies' },
  { id: 'dpo', title: '8. Contato do Encarregado (DPO)' },
];

export function PrivacyPage() {
  return (
    <LegalPageLayout
      badge="Segurança da Informação & LGPD"
      title="Política de Privacidade e Proteção de Dados"
      lastUpdated="Outubro de 2026"
      tocItems={TOC_ITEMS}
    >
      <div className="p-4 sm:p-5 rounded-2xl bg-neutral-100/80 dark:bg-neutral-900 border border-border/80 text-sm leading-relaxed space-y-3">
        <p>
          A <strong>Medikei Saúde Digital</strong> compreende a criticidade e a sensibilidade dos dados confiados a nós por nossos usuários e profissionais de saúde. Esta Política de Privacidade descreve com total transparência como coletamos, tratamos, armazenamos, compartilhamos e protegemos seus dados pessoais e dados pessoais sensíveis de saúde, em estrita conformidade com a <strong>Lei Geral de Proteção de Dados Pessoais (LGPD - Lei nº 13.709/2018)</strong>, o <strong>Marco Civil da Internet (Lei nº 12.965/2014)</strong> e as <strong>Resoluções do Conselho Federal de Medicina (CFM nº 2.314/2022)</strong>.
        </p>
      </div>

      {/* 1. Dados Pessoais que Coletamos */}
      <section id="dados-coletados" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Eye className="w-5 h-5 text-primary" />
          <span>1. Dados Pessoais que Coletamos</span>
        </h2>
        <p className="text-muted-foreground">
          Dividimos as categorias de dados coletados conforme a finalidade assistencial e operacional:
        </p>

        <div className="space-y-3 text-sm sm:text-base">
          <div className="p-4 rounded-xl border border-border bg-card">
            <h3 className="font-bold text-foreground mb-1.5">1.1 Dados Cadastrais e de Identificação</h3>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>
                <strong>Para Pacientes:</strong> Nome completo, CPF, data de nascimento, sexo/gênero, endereço residencial completo, número de telefone celular (WhatsApp), endereço de e-mail e dados de cobrança.
              </li>
              <li>
                <strong>Para Profissionais de Saúde:</strong> Nome completo, CPF, número de inscrição no CRM/conselho de classe e respectivo estado (UF), especialidades/RQE, dados bancários/chave PIX para repasses e certificados digitais.
              </li>
              <li>
                <strong>Para Afiliados/Parceiros:</strong> Nome/Razão Social, CPF/CNPJ, e-mail, telefone e chave PIX para comissionamento.
              </li>
            </ul>
          </div>

          <div className="p-4 rounded-xl border border-border bg-card">
            <h3 className="font-bold text-foreground mb-1.5">1.2 Dados Pessoais Sensíveis (Dados de Saúde)</h3>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>
                <strong>Histórico Clínico e Prontuário:</strong> Anamnese, queixas principais, histórico de patologias prévias, alergias, medicações em uso contínuo, diagnósticos provisórios ou definitivos, hipóteses diagnósticas e condutas.
              </li>
              <li>
                <strong>Documentos Emitidos:</strong> Receitas de medicamentos, pedidos de exames laboratoriais/imagem, laudos, atestados médicos e relatórios de acompanhamento emitidos na plataforma.
              </li>
            </ul>
          </div>

          <div className="p-4 rounded-xl border border-border bg-card">
            <h3 className="font-bold text-foreground mb-1.5">1.3 Dados de Conexão, Dispositivo e Navegação</h3>
            <p className="text-muted-foreground">
              Endereço IP, data e hora de cada acesso, geolocalização aproximada ou exata (utilizada para viabilizar e rastrear o deslocamento na Consulta Domiciliar), tipo de navegador, sistema operacional e identificadores de cookies.
            </p>
          </div>
        </div>
      </section>

      {/* 2. Bases Legais e Finalidades do Tratamento */}
      <section id="bases-legais" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Database className="w-5 h-5 text-primary" />
          <span>2. Bases Legais e Finalidades do Tratamento</span>
        </h2>
        <p className="text-muted-foreground">
          Tratamos seus dados exclusivamente mediante bases legais válidas previstas na LGPD:
        </p>

        <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
          <table className="w-full text-left text-xs sm:text-sm border-collapse rounded-2xl overflow-hidden border border-border bg-card shadow-xs">
            <thead className="bg-muted/80 text-foreground font-bold border-b border-border">
              <tr>
                <th className="p-3.5">Categoria do Dado</th>
                <th className="p-3.5">Finalidade Principal</th>
                <th className="p-3.5">Base Legal (LGPD)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60 text-muted-foreground">
              <tr>
                <td className="p-3.5 font-semibold text-foreground">Dados Cadastrais</td>
                <td className="p-3.5">Criação de conta, identificação segura, emissão de cobrança e agendamento de consultas.</td>
                <td className="p-3.5 text-primary font-medium">Execução de Contrato (Art. 7º, V)</td>
              </tr>
              <tr>
                <td className="p-3.5 font-semibold text-foreground">Dados de Saúde / Prontuário</td>
                <td className="p-3.5">Prestação de assistência médica, realização de teleconsultas, emissão de receitas e guarda obrigatória de prontuário.</td>
                <td className="p-3.5 text-emerald-600 dark:text-emerald-400 font-medium">
                  Tutela da Saúde (Art. 11, II, &quot;f&quot;) e Cumprimento de Obrigação Legal (Art. 7º, II / CFM)
                </td>
              </tr>
              <tr>
                <td className="p-3.5 font-semibold text-foreground">Dados Financeiros / Repasses</td>
                <td className="p-3.5">Processamento de pagamento de consultas e pagamento de comissões de parceiros.</td>
                <td className="p-3.5 text-primary font-medium">Execução de Contrato (Art. 7º, V) e Obrigação Legal Fiscal</td>
              </tr>
              <tr>
                <td className="p-3.5 font-semibold text-foreground">Registros de Acesso (IP)</td>
                <td className="p-3.5">Rastreamento contra fraudes e cumprimento de requisições judiciais.</td>
                <td className="p-3.5 text-primary font-medium">Marco Civil da Internet (Art. 15)</td>
              </tr>
              <tr>
                <td className="p-3.5 font-semibold text-foreground">Comunicações / Lembretes</td>
                <td className="p-3.5">Notificação de horário de consulta, lembrete de retorno e envio de receitas por WhatsApp/e-mail.</td>
                <td className="p-3.5 text-primary font-medium">Legítimo Interesse (Art. 7º, IX) e Execução de Contrato</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* 3. Segurança e Confidencialidade das Teleconsultas */}
      <section id="seguranca" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" />
          <span>3. Segurança e Confidencialidade das Teleconsultas</span>
        </h2>
        <ul className="space-y-2.5 list-disc pl-5 text-sm sm:text-base">
          <li>
            <strong>Criptografia em Trânsito e Repouso:</strong> Todas as sessões de teleconsulta ocorrem via protocolos seguros com criptografia de ponta a ponta (WebRTC / TLS 1.3). As videoconferências são de visualização instantânea e não são gravadas pela Medikei, assegurando o estrito sigilo da relação médico-paciente.
          </li>
          <li>
            <strong>Segurança do Prontuário:</strong> Os registros de saúde e prontuários digitais são protegidos por bancos de dados isolados, criptografados e com controle rígido de controle de acesso (RBAC - Role-Based Access Control).
          </li>
          <li>
            <strong>Sigilo Profissional:</strong> Todos os profissionais credenciados estão sujeitos ao sigilo médico profissional preconizado pelo Código de Ética Médica e resoluções do CFM.
          </li>
        </ul>
      </section>

      {/* 4. Compartilhamento de Dados */}
      <section id="compartilhamento" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Share2 className="w-5 h-5 text-primary" />
          <span>4. Compartilhamento de Dados</span>
        </h2>
        <p className="font-semibold text-foreground">
          A Medikei não vende, não aluga e não comercializa dados pessoais sob nenhuma hipótese.
        </p>
        <p className="text-muted-foreground">
          O compartilhamento ocorre exclusivamente quando indispensável para a prestação do serviço de saúde:
        </p>
        <ul className="space-y-2 list-disc pl-5 text-sm sm:text-base">
          <li>
            <strong>Com o Profissional de Saúde:</strong> O médico selecionado para a consulta recebe acesso ao prontuário do paciente antes e durante o atendimento para prestar assistência médica adequada.
          </li>
          <li>
            <strong>Gateways de Pagamento:</strong> Provedores de pagamento parceiros para processamento de transações via PIX e cartão de crédito, aplicando regras de segurança antifraude (padrão PCI-DSS).
          </li>
          <li>
            <strong>Serviços de Assinatura Digital e Validação:</strong> Emissoras de certificados digitais e autoridades certificadoras vinculadas ao padrão ICP-Brasil para validação pública de atestados e receitas.
          </li>
          <li>
            <strong>Autoridades Governamentais e Judiciais:</strong> Mediante ordem judicial fundamentada ou exigência legal regulatória específica.
          </li>
        </ul>
      </section>

      {/* 5. Retenção e Guarda dos Dados */}
      <section id="retencao" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" />
          <span>5. Retenção e Guarda dos Dados</span>
        </h2>
        <ul className="space-y-2.5 list-disc pl-5 text-sm sm:text-base">
          <li>
            <strong>Prontuário Eletrônico:</strong> Em estrita observância à Lei Federal nº 13.787/2018 e resoluções do CFM, os prontuários médicos e dados de saúde correlatos devem ser mantidos pelo prazo mínimo de <strong>20 (vinte) anos</strong> a partir do último registro.
          </li>
          <li>
            <strong>Registros de Conexão:</strong> Registros de logs de IP são mantidos sob sigilo pelo período legal obrigatório de <strong>6 (seis) meses</strong>, conforme o Marco Civil da Internet.
          </li>
          <li>
            <strong>Demais Dados Cadastrais:</strong> Mantidos enquanto durar o relacionamento com o usuário ou até que o mesmo solicite sua exclusão (ressalvada a guarda para cumprimento de obrigações fiscais e regulatórias).
          </li>
        </ul>
      </section>

      {/* 6. Direitos do Titular de Dados (Art. 18 da LGPD) */}
      <section id="direitos" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <UserCheck className="w-5 h-5 text-primary" />
          <span>6. Direitos do Titular de Dados (Art. 18 da LGPD)</span>
        </h2>
        <p className="text-muted-foreground">
          Você, como titular de dados pessoais, tem o direito de solicitar a qualquer momento:
        </p>
        <ul className="space-y-2 list-disc pl-5 text-sm sm:text-base">
          <li>Confirmação da existência de tratamento e acesso aos seus dados;</li>
          <li>Correção de dados incompletos, inexatos ou desatualizados;</li>
          <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade;</li>
          <li>Portabilidade dos dados a outro prestador de serviços;</li>
          <li>Informação sobre o compartilhamento de dados com entidades públicas ou privadas;</li>
          <li>Revogação do consentimento, quando esta for a base legal aplicável.</li>
        </ul>
        <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-950 dark:text-amber-200 text-xs sm:text-sm">
          <strong>Nota Legal:</strong> A solicitação de exclusão de prontuários médicos não poderá ser atendida quando houver imperativo legal de guarda pelo prazo regulamentar do CFM (20 anos).
        </div>
      </section>

      {/* 7. Política de Cookies e Rastreamento */}
      <section id="cookies" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          <span>7. Política de Cookies e Rastreamento</span>
        </h2>
        <p>
          Utilizamos cookies técnicos estritamente necessários para autenticação de sessão e segurança, além de cookies analíticos para entender a usabilidade e aprimorar a experiência da plataforma. O usuário pode desativar ou configurar cookies por meio das configurações de seu navegador de internet a qualquer momento.
        </p>
      </section>

      {/* 8. Contato do Encarregado de Proteção de Dados (DPO) */}
      <section id="dpo" className="space-y-4 pt-4 border-t border-border/60">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
          <Mail className="w-5 h-5 text-primary" />
          <span>8. Contato do Encarregado de Proteção de Dados (DPO)</span>
        </h2>
        <p className="text-muted-foreground">
          Para exercer seus direitos de privacidade, tirar dúvidas ou apresentar solicitações sobre o tratamento de seus dados pessoais pela Medikei, entre em contato diretamente com nosso Encarregado:
        </p>
        <div className="p-4 sm:p-5 rounded-2xl bg-neutral-100/80 dark:bg-neutral-900 border border-border/80 space-y-2 text-sm">
          <p>
            <strong>E-mail do DPO / Privacidade:</strong>{' '}
            <a href="mailto:privacidade@medikei.com.br" className="text-primary font-semibold hover:underline">
              privacidade@medikei.com.br
            </a>{' '}
            (ou{' '}
            <a href="mailto:dpo@medikei.com.br" className="text-primary font-semibold hover:underline">
              dpo@medikei.com.br
            </a>)
          </p>
          <p>
            <strong>Canal de Atendimento:</strong> Central de Suporte e Ajuda da Medikei Saúde Digital
          </p>
        </div>
      </section>
    </LegalPageLayout>
  );
}
